import {
  CenterServiceRequisition,
  CenterServiceRequisitionListResponse,
  ApiCenterServiceRequisitionListResponse,
  CenterServiceRequisitionQueryParams,
} from './centerServiceRequisitions.dto';
import { API_BASE_URL } from '@/config/env';

class CenterServiceRequisitionsService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText || '{}');
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          if (this.logoutCallback) this.logoutCallback();
        }
        const message = Array.isArray(errorData.message)
          ? errorData.message.join(', ')
          : errorData.message;
        if (message) throw new Error(message);
      } catch (error) {
        if (error instanceof Error && error.message !== errorText) {
          throw error;
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      if (this.logoutCallback) this.logoutCallback();
    }
    return data;
  }

  async getRequisitions(
    token: string,
    params: CenterServiceRequisitionQueryParams = {}
  ): Promise<CenterServiceRequisitionListResponse> {
    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.diagnosticCenterId !== undefined)
      queryString.append(
        'diagnosticCenterId',
        params.diagnosticCenterId.toString()
      );
    if (params.serviceId !== undefined)
      queryString.append('serviceId', params.serviceId.toString());
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/center-service-requisitions${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    const response =
      await this.request<ApiCenterServiceRequisitionListResponse>(endpoint, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

    const responseData = response.data as any;
    if (!responseData) {
      return { data: [], meta: { total: 0, page: 1, lastPage: 1, limit: 10 } };
    }
    if (Array.isArray(responseData)) {
      return {
        data: responseData,
        meta: { total: responseData.length, page: 1, lastPage: 1, limit: 10 },
      };
    }
    return {
      data: responseData.data || [],
      meta: responseData.meta || { total: 0, page: 1, lastPage: 1, limit: 10 },
    };
  }

  async getRequisition(
    token: string,
    id: number
  ): Promise<CenterServiceRequisition> {
    const response = await this.request<{ data: CenterServiceRequisition }>(
      `/admin/center-service-requisitions/${id}`,
      { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }

  async createRequisition(
    token: string,
    payload: Partial<CenterServiceRequisition>
  ): Promise<CenterServiceRequisition> {
    const response = await this.request<{ data: CenterServiceRequisition }>(
      '/admin/center-service-requisitions',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      }
    );
    return response.data;
  }

  async updateRequisition(
    token: string,
    id: number,
    payload: Partial<CenterServiceRequisition>
  ): Promise<CenterServiceRequisition> {
    const response = await this.request<{ data: CenterServiceRequisition }>(
      `/admin/center-service-requisitions/${id}`,
      {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      }
    );
    return response.data;
  }

  async deleteRequisition(
    token: string,
    id: number
  ): Promise<{ message: string }> {
    const response = await this.request<{ message: string }>(
      `/admin/center-service-requisitions/${id}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
    );
    return { message: response.message };
  }
}

export const centerServiceRequisitionsService =
  new CenterServiceRequisitionsService();
